import React from 'react';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import useCancelTour from '@/features/dashboard/tourConfig/hooks/useCancelTour.ts';

const cancelTourSchema = z
  .object({
    sendMessage: z.boolean(),
    message: z.string().optional(),
  })
  .refine(
    (data) =>
      !data.sendMessage || (data.message && data.message.trim().length > 0),
    {
      message: 'Wiadomość jest wymagana',
      path: ['message'],
    },
  );

type CancelTourFormData = z.infer<typeof cancelTourSchema>;

const CancelTourModal = ({
  tourId,
  tourName,
  onClose,
}: {
  tourId: string;
  tourName: string;
  onClose: () => void;
}) => {
  const cancelTourMutation = useCancelTour();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CancelTourFormData>({
    resolver: zodResolver(cancelTourSchema),
    defaultValues: {
      sendMessage: false,
      message: '',
    },
  });

  const sendMessage = watch('sendMessage');

  const onSubmit = (data: CancelTourFormData) => {
    cancelTourMutation.mutate(
      {
        tourId,
        request: {
          sendMessage: data.sendMessage,
          message: data.sendMessage ? data.message : undefined,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader title="Anuluj wyjazd" onClose={onClose} />
        <ModalContent>
          <p className="text-sm text-neutral-900">
            Czy na pewno chcesz anulować wyjazd <b>{tourName}</b>? Rejestracja
            zostanie automatycznie zamknięta.
          </p>
          <CheckboxInput
            label="Wyślij wiadomość do uczestników"
            register={register('sendMessage')}
          />
          {sendMessage && (
            <TextareaInput
              label="Wiadomość"
              register={register('message')}
              error={errors.message?.message}
              placeholder="Treść wiadomości do uczestników..."
              rows={4}
            />
          )}
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="danger"
            size="sm"
            type="submit"
            isLoading={cancelTourMutation.isPending}
          >
            Anuluj wyjazd
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CancelTourModal;
